import {useCallback, useEffect, useMemo, useState} from 'react';
import {Box, Typography} from '@mui/material';
import {t} from 'i18next';
import {GridColDef, GridFilterModel, GridSortModel} from '@mui/x-data-grid';
import './IssueCodeDataGrid.scss';
import {tableProps} from '../../job-list-layout/utils';
import ArrDataGridPro from '../../../../common/arr-data-grid-pro/ArrDataGridPro';
import ArrButton from '../../../../common/arr-button/ArrButton';
import {getColumns} from './issueCodeColumn';
import {relevancyColumns} from './relevancyColumn';
import {DataGridRowSelectionModel} from '../../../../../shared/interfaces';
import {
  useGetAIReviewJobResultIssueCodeList,
  useGetAIReviewJobResultRelevancyList,
} from '../../../../../hooks/ai/review/useReview';
import {ISSUE_CODE_COLUMN_MAP, RELEVANCY_COLUMN_MAP} from '../../utils';
import {ToastType} from '../../../../../shared/constants';
import ArrToastNotification from '../../../../common/arr-toast-notification/ArrToastNotification';

interface IJobList {
  jobId: string | number;
  type: 'Issue Coding' | 'Relevancy';
}

type ToastState = {
  open: boolean;
  message: string;
  status: ToastType;
};

const defaultToastState: ToastState = {
  open: false,
  message: '',
  status: ToastType.Success,
};

const ISSUE_CODING = 'Issue Coding';
const RELEVANCY = 'Relevancy';

const IssueCodeDataGrid = ({jobId, type}: IJobList) => {
  const [pageSize, setPageSize] = useState(tableProps.DEFAULT_PAGE_SIZE);
  const [pageNumber, setPageNumber] = useState(1);

  const [sortModel, setSortModel] = useState<GridSortModel>([
    {
      field: type === ISSUE_CODING ? 'relevantDocCount' : 'documentId',
      sort: 'asc',
    },
  ]);

  const [filterModel, setFilterModel] = useState<GridFilterModel>({
    items: [],
  });

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [unselectedIds, setUnselectedIds] = useState<string[]>([]);
  const [isAllSelected, setIsAllSelected] = useState(false);

  const isIssueCoding = type === ISSUE_CODING;
  const isRelevancy = type === RELEVANCY;

  const [toast, setToast] = useState<ToastState>(defaultToastState);

  const showErrorToast = useCallback((message: string) => {
    setToast({
      open: true,
      message,
      status: ToastType.Error,
    });
  }, []);

  const issueCodePayload = useMemo(
    () => ({
      sortingColumn: ISSUE_CODE_COLUMN_MAP[sortModel[0]?.field],
      direction: sortModel[0]?.sort?.toUpperCase() ?? 'ASC',
      columnFilter: filterModel.items
        .filter((i) => ISSUE_CODE_COLUMN_MAP[i.field])
        .map((i) => ({
          columnName: ISSUE_CODE_COLUMN_MAP[i.field],
          columnValue: String(i.value ?? ''),
        })),
    }),
    [sortModel, filterModel]
  );

  const relevancyPayload = useMemo(
    () => ({
      sortingColumn: sortModel[0]?.field,
      direction: sortModel[0]?.sort?.toUpperCase() ?? 'ASC',
      displayLength: pageSize,
      pageNumber,
      columnFilter: filterModel.items
        .filter((i) => RELEVANCY_COLUMN_MAP[i.field])
        .map((i) => ({
          columnName: RELEVANCY_COLUMN_MAP[i.field],
          columnValue: String(i.value ?? ''),
        })),
    }),
    [sortModel, filterModel, pageSize, pageNumber]
  );

  const {
    data: issueCodeResponse,
    isLoading: isIssueCodeLoading,
    isError: isIssueCodeError,
  } = useGetAIReviewJobResultIssueCodeList(jobId, issueCodePayload, type);

  const {
    data: relevancyResponse,
    isLoading: isRelevancyLoading,
    isError: isRelevancyError,
  } = useGetAIReviewJobResultRelevancyList(jobId, relevancyPayload, type);

  const rows = useMemo(() => {
    if (isIssueCoding) {
      return issueCodeResponse?.issueCodeResults ?? [];
    }
    if (isRelevancy) {
      return relevancyResponse?.documents ?? [];
    }
    return [];
  }, [isIssueCoding, isRelevancy, issueCodeResponse, relevancyResponse]);

  const [checkboxState, setCheckboxState] = useState<Record<string, boolean>>(
    {}
  );

  const handleCellCheckboxChange = useCallback(
    (rowId: number, columnKey: string, checked: boolean) => {
      setCheckboxState((prev) => ({
        ...prev,
        [`${rowId}-${columnKey}`]: checked,
      }));
    },
    []
  );

  const handleHeaderCheckboxChange = useCallback(
    (columnKey: string, checked: boolean) => {
      setCheckboxState((prev) => {
        const next = {...prev};

        rows.forEach((row: any) => {
          next[`${row.documentId}-${columnKey}`] = checked;
        });

        return next;
      });
    },
    [rows]
  );

  const handleSelectionStateChange = useCallback(
    (state: DataGridRowSelectionModel) => {
      setSelectedIds(state.selectedIds);
      setUnselectedIds(state.unselectedIds);
      setIsAllSelected(state.isAllSelected);
    },
    []
  );
  const isViewInDocListEnabled = useMemo(() => {
    if (isRelevancy) {
      return isAllSelected || selectedIds.length > 0;
    }

    if (isIssueCoding) {
      return Object.values(checkboxState).some(Boolean);
    }

    return false;
  }, [type, isAllSelected, selectedIds, checkboxState]);

  useEffect(() => {
    if (type === ISSUE_CODING && isIssueCodeError) {
      showErrorToast(t('ai.review.jobResult.error.failedToFetch'));
    }
  }, [type, isIssueCodeError, showErrorToast]);

  useEffect(() => {
    if (type === RELEVANCY && isRelevancyError) {
      showErrorToast(t('ai.review.jobResult.error.failedToFetch'));
    }
  }, [type, isRelevancyError, showErrorToast]);

  const columns: GridColDef[] = useMemo(() => {
    return isIssueCoding
      ? getColumns(
          checkboxState,
          handleCellCheckboxChange,
          handleHeaderCheckboxChange,
          rows
        )
      : relevancyColumns();
  }, [
    type,
    checkboxState,
    handleCellCheckboxChange,
    handleHeaderCheckboxChange,
    rows,
  ]);

  return (
    <>
      <Box
        className="issue-code-container"
        data-testid={`issue-code-data-grid-container`}
      >
        <Box
          className="issue-code-header"
          data-testid={`issue-code-data-grid-header`}
        >
          <Typography
            className="typo-text subhead4_size"
            tabIndex={0}
            data-testid={`issue-code-data-grid-header-name`}
          >
            {t('ai.review.jobTypeDataGrid.headerName')}
          </Typography>

          <Box
            className="action-buttons-box"
            tabIndex={0}
            data-testid={`issue-code-action-button-box`}
          >
            <ArrButton
              variant="primary"
              size="medium"
              className="view-doc-list-btn"
              disabled={!isViewInDocListEnabled}
              aria-label={t('ai.investigate.viewInDocList')}
              dataTestId="doclist-view"
            >
              {t('ai.investigate.viewInDocList')}
            </ArrButton>
          </Box>
        </Box>
        <ArrDataGridPro
          rowHeight={tableProps.ROW_HEIGHT}
          rows={rows ?? []}
          columns={columns}
          getRowClassName={(params) => {
            if (isAllSelected) {
              return 'row-checkbox-selected';
            }

            return selectedIds.includes(params.id as string)
              ? 'row-checkbox-selected'
              : '';
          }}
          rowCount={
            isRelevancy
              ? relevancyResponse?.documents?.length
              : issueCodeResponse?.issueCodeResults?.length
          }
          primaryKey="documentId"
          pagination={isRelevancy}
          paginationMode="server"
          sortingMode="server"
          filterMode="server"
          loading={isIssueCodeLoading || isRelevancyLoading}
          enableCheckbox={isRelevancy}
          selectedIds={selectedIds}
          unselectedIds={unselectedIds}
          isAllSelected={isAllSelected}
          onSelectionStateChange={handleSelectionStateChange}
          itemsPerPage={pageSize}
          setItemsPerPage={setPageSize}
          currentPage={pageNumber}
          showPerPageOptions
          pageSizeOptions={tableProps.PAGE_SIZE_OPTIONS}
          onPageChange={setPageNumber}
          sortModel={sortModel}
          onSortModelChange={setSortModel}
          filterModel={filterModel}
          onFilterModelChange={setFilterModel}
          headerFilters
          headerFilterHeight={tableProps.FILTER_HEADER_HEIGHT}
          disableVirtualization
          disableColumnResize={false}
          disableColumnReorder
          disableColumnFilter
          showCellVerticalBorder
          entityType={t('common.entries')}
        />
      </Box>

      {toast.open && (
        <ArrToastNotification
          message={toast.message}
          status={[toast.status]}
          onClose={() => setToast(defaultToastState)}
        />
      )}
    </>
  );
};

export default IssueCodeDataGrid;
